import { logger } from '../../../lib/logger';
import { CONTACT_TYPE_OPTIONS } from '../PatientProfile/Account/Contacts/constants';

export type FieldValueType =
  | 'string'
  | 'number'
  | 'enum'
  | 'date'
  | 'boolean'
  | 'email'
  | 'phone'
  | 'url';

export type ConditionFieldOption = {
  key: string;
  label: string;
  valueType: FieldValueType;
  operators?: string[];
  options?: Array<{ value: string; label: string }>;
};

export type ActionParamControl =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'email'
  | 'phone'
  | 'url'
  | 'tag'
  | 'mail_template'
  | 'practitioner_form';

export type ActionParamSchema = {
  key: string;
  label: string;
  control: ActionParamControl;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  helperText?: string;
};

export type ActionSchema = {
  type: string;
  label: string;
  params: ActionParamSchema[];
};

export const TRIGGER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'assessment_completed', label: 'Assessment completed' },
  { value: 'assessment_submitted', label: 'Assessment submitted' },
  { value: 'appointment_scheduled', label: 'Appointment scheduled' },
  { value: 'appointment_missed', label: 'Appointment missed' },
  { value: 'session_completed', label: 'Session completed' },
  { value: 'client_tag_added', label: 'Client tag added' },
  { value: 'client_tag_removed', label: 'Client tag removed' },
  { value: 'client_created', label: 'Client created' },
  { value: 'invoice_paid', label: 'Invoice paid' },
  { value: 'practitioner_assigned', label: 'Practitioner assigned to client' },
  { value: 'service_booked', label: 'Service booked' },
  // Time-based triggers
  { value: 'program_start_date', label: 'Program starts' },
  { value: 'program_end_date', label: 'Program ends' },
];

/**
 * Trigger events shown as "Coming soon" at bottom of trigger dropdown (disabled).
 */
const HIDDEN_TRIGGER_EVENTS = [
  'assessment_completed',
  'assessment_submitted',
  'appointment_scheduled',
  'appointment_missed',
  'session_completed',
  'invoice_paid',
];
export const TRIGGER_OPTIONS_FOR_BUILDER = TRIGGER_OPTIONS.filter(
  (o) => !HIDDEN_TRIGGER_EVENTS.includes(o.value),
);
export const TRIGGER_OPTIONS_COMING_SOON = TRIGGER_OPTIONS.filter((o) =>
  HIDDEN_TRIGGER_EVENTS.includes(o.value),
);

export const CONDITION_FIELDS_BY_TRIGGER: Record<
  string,
  ConditionFieldOption[]
> = {
  assessment_completed: [
    {
      key: 'assessment.type',
      label: 'Assessment type',
      valueType: 'enum',
      options: [
        { value: 'PHQ9', label: 'PHQ-9' },
        { value: 'GAD7', label: 'GAD-7' },
        { value: 'PCL5', label: 'PCL-5' },
      ],
    },
    { key: 'assessment.score', label: 'Assessment score', valueType: 'number' },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  assessment_submitted: [
    {
      key: 'assessment.type',
      label: 'Assessment type',
      valueType: 'enum',
      options: [
        { value: 'PHQ9', label: 'PHQ-9' },
        { value: 'GAD7', label: 'GAD-7' },
        { value: 'PCL5', label: 'PCL-5' },
      ],
    },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  appointment_scheduled: [
    { key: 'appointment_id', label: 'Appointment ID', valueType: 'string' },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  appointment_missed: [
    { key: 'appointment_id', label: 'Appointment ID', valueType: 'string' },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  session_completed: [
    { key: 'session_id', label: 'Session ID', valueType: 'string' },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  client_tag_added: [
    {
      key: 'tag_id',
      label: 'Tag',
      valueType: 'string',
      operators: ['eq'],
    },
  ],
  client_tag_removed: [
    {
      key: 'tag_id',
      label: 'Tag',
      valueType: 'string',
      operators: ['eq'],
    },
  ],
  // Legacy trigger support for existing saved rules.
  client_tag_changed: [
    { key: 'tag_name', label: 'Tag name', valueType: 'string' },
    {
      key: 'action',
      label: 'Tag action',
      valueType: 'enum',
      options: [
        { value: 'added', label: 'Added' },
        { value: 'removed', label: 'Removed' },
      ],
    },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  client_created: [
    { key: 'client.email', label: 'Client email', valueType: 'email' },
    { key: 'client.first_name', label: 'First name', valueType: 'string' },
    { key: 'client.last_name', label: 'Last name', valueType: 'string' },
    { key: 'client.phone_number', label: 'Phone number', valueType: 'phone' },
    { key: 'client.created_at', label: 'Created at', valueType: 'date' },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
  ],
  invoice_paid: [
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
    { key: 'program_invoice_id', label: 'Invoice ID', valueType: 'string' },
    { key: 'amount', label: 'Amount', valueType: 'number' },
    { key: 'currency', label: 'Currency', valueType: 'string' },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
  ],
  // practitioner_assigned: simplified to "Which practitioner?" and "Which client?" dropdowns (no generic conditions)
  practitioner_assigned: [],
  service_booked: [
    {
      key: 'provider_service_id',
      label: 'Service',
      valueType: 'string',
      operators: ['contains'],
    },
    { key: 'program_id', label: 'Program ID', valueType: 'string' },
    {
      key: 'calendly_event_type_name',
      label: 'Event type name',
      valueType: 'string',
    },
    { key: 'patient_id', label: 'Client ID', valueType: 'string' },
  ],
  // Time-based triggers use a dedicated optional tag picker, not generic conditions
  program_start_date: [],
  program_end_date: [],
};

export const DEFAULT_CONDITION_OPERATORS = ['eq', 'ne', 'contains'];

export const OPERATOR_LABELS: Record<string, string> = {
  eq: 'Is equal to',
  ne: 'Is not equal to',
  gt: 'Is greater than',
  gte: 'Is greater than or equal to',
  lt: 'Is less than',
  lte: 'Is less than or equal to',
  in: 'Is one of',
  contains: 'Contains',
  not_contains: 'Does not contain',
};

export const OPERATORS_BY_VALUE_TYPE: Partial<
  Record<FieldValueType, string[]>
> = {
  number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
  date: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'],
  enum: ['eq', 'ne', 'in'],
  boolean: ['eq', 'ne'],
  email: ['eq', 'ne', 'contains'],
  phone: ['eq', 'ne', 'contains'],
  url: ['eq', 'ne', 'contains'],
  string: ['eq', 'ne', 'contains', 'not_contains', 'in'],
};

export const ACTION_SCHEMAS: ActionSchema[] = [
  {
    type: 'assign_tag',
    label: 'Apply tag',
    params: [
      {
        key: 'tag_ids',
        label: 'Tag',
        control: 'tag',
        required: true,
      },
      {
        key: 'entity_id_field',
        label: 'Target entity field',
        control: 'select',
        required: false,
        options: [{ value: 'program_id', label: 'Program ID' }],
      },
    ],
  },
  {
    type: 'send_notification',
    label: 'Send notification',
    params: [
      {
        key: 'recipient',
        label: 'Recipient',
        control: 'select',
        required: true,
        options: [
          { value: 'provider', label: 'Provider team' },
          { value: 'client', label: 'Client' },
        ],
      },
      {
        key: 'title',
        label: 'Title',
        control: 'text',
        required: false,
      },
      {
        key: 'message',
        label: 'Message',
        control: 'textarea',
        required: true,
      },
    ],
  },
  {
    type: 'send_email',
    label: 'Send email',
    params: [
      {
        key: 'to',
        label: 'Recipient email',
        control: 'email',
        required: true,
      },
      { key: 'subject', label: 'Subject', control: 'text', required: true },
      { key: 'body', label: 'Body', control: 'textarea', required: true },
    ],
  },
  {
    type: 'send_sms',
    label: 'Send SMS',
    params: [
      {
        key: 'to',
        label: 'Phone number',
        control: 'phone',
        required: true,
      },
      { key: 'message', label: 'Message', control: 'textarea', required: true },
    ],
  },
  {
    type: 'send_template',
    label: 'Send Mail',
    params: [
      {
        key: 'follow_up_template_id',
        label: 'Mail template',
        control: 'mail_template',
        required: true,
        placeholder: 'Select Mail template',
      },
    ],
  },
  {
    type: 'send_contact_form',
    label: 'Send form to Contact',
    params: [
      {
        key: 'assessment_id',
        label: 'Form',
        control: 'practitioner_form',
        required: true,
        placeholder: 'Select practitioner form',
      },
      {
        key: 'contact_type',
        label: 'Contact type',
        control: 'select',
        required: true,
        options: CONTACT_TYPE_OPTIONS.map((o) => ({
          value: o.value,
          label: o.label,
        })),
        helperText:
          'Sends to the oldest contact of this type with an email. Ensure the contact exists before this rule fires.',
      },
    ],
  },
  {
    type: 'update_field',
    label: 'Update field',
    params: [
      {
        key: 'field',
        label: 'Field',
        control: 'select',
        required: true,
        options: [
          { value: 'client.first_name', label: 'Client first name' },
          { value: 'client.last_name', label: 'Client last name' },
          { value: 'client.phone_number', label: 'Client phone number' },
          { value: 'client.email', label: 'Client email' },
          {
            value: 'program.referral_source',
            label: 'Program referral source',
          },
          { value: 'program.timezone', label: 'Program timezone' },
        ],
      },
      {
        key: 'value',
        label: 'New value',
        control: 'text',
        required: true,
      },
    ],
  },
  {
    type: 'create_invoice',
    label: 'Create invoice',
    params: [
      {
        key: 'amount',
        label: 'Amount',
        control: 'number',
        required: true,
        helperText: 'Invoice amount (e.g. 150 for $150.00)',
      },
      {
        key: 'note',
        label: 'Note / description',
        control: 'textarea',
        required: false,
        placeholder: 'Optional note (max 500 characters)',
      },
    ],
  },
];

export const ACTION_SCHEMA_BY_TYPE: Record<string, ActionSchema> =
  ACTION_SCHEMAS.reduce((acc, action) => {
    acc[action.type] = action;
    return acc;
  }, {} as Record<string, ActionSchema>);

/**
 * Action types shown as "Coming soon" at bottom of action dropdown (disabled).
 */
const HIDDEN_ACTION_TYPES = [
  'send_sms',
  'send_email',
  'send_notification',
  'update_field',
  'create_invoice',
];
export const ACTION_SCHEMAS_FOR_BUILDER: ActionSchema[] = ACTION_SCHEMAS.filter(
  (s) => !HIDDEN_ACTION_TYPES.includes(s.type),
);
export const ACTION_SCHEMAS_COMING_SOON: ActionSchema[] = ACTION_SCHEMAS.filter(
  (s) => HIDDEN_ACTION_TYPES.includes(s.type),
);

export const DEFAULT_TAG_COLOR = '#F4F3F1';

export const TIME_TRIGGER_OFFSET_MAX = 365;

export const TIME_TRIGGER_TIMING_OPTIONS = [
  { value: 'before', label: 'Before' },
  { value: 'on_date', label: 'On date' },
  { value: 'after', label: 'After' },
] as const;

export type TimeTriggerTiming =
  typeof TIME_TRIGGER_TIMING_OPTIONS[number]['value'];

export const TIME_BASED_TRIGGER_EVENTS = [
  'program_start_date',
  'program_end_date',
] as const;

export type TimeBasedTriggerEvent = typeof TIME_BASED_TRIGGER_EVENTS[number];

export interface TimeTriggerConfig {
  offset: number;
  time: string;
}

const HHMM_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidTimeString(time: string): boolean {
  return HHMM_REGEX.test(time);
}

/**
 * Parse and validate a triggerConfig JSON string into a typed object.
 * Returns null if the config is missing, malformed, or has invalid shape.
 */
export function parseTimeTriggerConfig(
  raw: string | null | undefined,
): TimeTriggerConfig | null {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      logger.warn('parseTimeTriggerConfig: parsed value is not an object', {
        parsed,
      });
      return null;
    }
    const { offset, time } = parsed;
    if (typeof offset !== 'number' || !Number.isFinite(offset)) {
      logger.warn('parseTimeTriggerConfig: invalid offset', { offset });
      return null;
    }
    if (!Number.isInteger(offset)) {
      logger.warn('parseTimeTriggerConfig: non-integer offset', { offset });
      return null;
    }
    if (typeof time !== 'string' || !isValidTimeString(time)) {
      logger.warn('parseTimeTriggerConfig: invalid time', { time });
      return null;
    }
    return { offset, time };
  } catch {
    logger.warn('parseTimeTriggerConfig: failed to parse triggerConfig JSON', {
      raw: raw.slice(0, 100),
    });
    return null;
  }
}

/**
 * Helper to check if a trigger is time-based
 */
export function isTimeBasedTrigger(
  triggerEvent: string,
): triggerEvent is TimeBasedTriggerEvent {
  return (TIME_BASED_TRIGGER_EVENTS as readonly string[]).includes(
    triggerEvent,
  );
}

/** Live "client has tag" condition ids on a time-based rule (AND). */
export function getTimeTriggerLiveTagIds(rule: {
  conditions?: {
    rules?: Array<{ field?: string | null; value?: string | null }>;
  } | null;
}): string[] {
  const rows = rule.conditions?.rules || [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    if (row.field !== 'client_tag_ids' && row.field !== 'tag_id') {
      continue;
    }
    const raw = (row.value ?? '').trim();
    if (!raw) {
      continue;
    }
    for (const part of raw.split(',')) {
      const id = part.trim();
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/** Join tag names for helper copy: "A", "A and B", "A, B, and C". */
export function formatTagNameList(names: string[]): string {
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Default triggerConfig for time-based triggers
 */
export const DEFAULT_TIME_TRIGGER_CONFIG: TimeTriggerConfig = {
  offset: 0,
  time: '09:00',
};
